import { TranslationDto } from '../translation.dto'

import { RewardDiscountType, RewardType } from './challenge-reward.dto'

/**
 * FR-07-BR-03 — trạng thái Reward Code.
 * Khớp enum `challenge_reward_code_status` (`ChallengeRewardCode.status`).
 */
export const REWARD_CODE_STATUS = {
  NOT_CLAIMED: 'NOT_CLAIMED',
  CLAIMED: 'CLAIMED',
  USED: 'USED'
} as const

export type RewardCodeStatus = typeof REWARD_CODE_STATUS[keyof typeof REWARD_CODE_STATUS]

/**
 * Discount **cấu hình** của một Reward Option (API #10).
 *
 * API #8 chỉ trả `usedMallName` / `usedOfflineTenantName` / `discountAmount` khi code đã
 * được dùng; code chưa dùng thì cả 3 đều `null` ⇒ popup code list lấy discount cấu hình này
 * làm fallback. Mall / Tenant không fallback — hiện `—` (QA 2026-10-23).
 */
export class RewardOptionScopeDto {
  public discountType?: RewardDiscountType
  public discountValue?: number

  public static create(data?: Partial<RewardOptionScopeDto>): RewardOptionScopeDto {
    const instance = new RewardOptionScopeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-01 — tổng hợp số quà đã phát theo từng Reward Option (BE aggregate).
 *
 * API #4 `GET /challenge-rewards/summary?challengeId&rewardOptionId`
 * (trước đây FE giả định `GET /challenges/:id/reward-history/summary`).
 */
export class RewardHistorySummaryDto {
  /** ⚠️ BE trả tên `rewardTierId`. */
  public challengeRewardTierId?: string
  /** `ChallengeRewardTier.position` — hiển thị "Tier 1", "Tier 2"... (FR-07-UI-01).
   *  ⚠️ BE trả tên `rewardTierPosition`. */
  public tierPosition?: number
  /** ⚠️ BE trả tên `rewardOptionId`. */
  public challengeRewardOptionId?: string
  /** ⚠️ BE trả `rewardOptionName: string` (đã localize) — service bọc thành TranslationDto. */
  public rewardName?: TranslationDto
  /** ✅ khớp BE. Dùng cho `isVirtualGift` ở dialog danh sách code. */
  public rewardType?: RewardType
  /**
   * Cần để format cột Discount trong danh sách code (% hay $).
   *
   * ⚠️ BE-MISSING (B9): API #4 **không trả** field này.
   * Tác động thật: `RewardCodeListDialogComponent.formatDiscount()` fallback về nhánh `%`
   * ⇒ Virtual Gift loại FIXED_AMOUNT render `50 %` thay vì `$50.00` — **sai dữ liệu**.
   * Workaround đang dùng: `AdminRewardHistoryService.summary()` nhận thêm tham số
   * `rewardTiers` từ API #10 để join `discountType` theo `rewardOptionId`.
   */
  public discountType?: RewardDiscountType
  /**
   * Tổng quà đã phát.
   * ⚠️ BE **không trả** field này — service tự tính `claimed + used`.
   */
  public redeemedCount?: number
  /** `ChallengeRewardOption.numberOfRewards`. ⚠️ BE trả tên `total`. */
  public totalCount?: number
  /** API #4 — code chưa ai claim. Dùng cho FR-10-BR-01 (sắp hết quà) / FR-07-BR-04. */
  public notClaimed?: number
  /** API #4 — đã claim nhưng chưa dùng. */
  public claimed?: number
  /** API #4 — đã mang đi dùng. */
  public used?: number
  /** API #4 — mới, cho phép summary cross-challenge. */
  public challengeId?: string
  public challengeName?: string

  public static create(data?: Partial<RewardHistorySummaryDto>): RewardHistorySummaryDto {
    const instance = new RewardHistorySummaryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-02 — một dòng của bảng `ChallengeRewardCode`.
 *
 * API #8 `GET /challenge-reward-options/:id/codes`
 * (trước đây FE giả định `GET /challenges/:cid/reward-options/:oid/codes` — path mới
 * **bỏ hẳn `challengeId`**).
 */
export class RewardCodeDto {
  /** ⚠️ BE trả tên `rewardId`. */
  public id?: string
  /** Biết từ path param, BE không trả trong row. */
  public challengeRewardOptionId?: string
  /** `ChallengeRewardCode.code` (unique). ✅ khớp BE. */
  public code?: string
  /**
   * Mã Reward Option hiển thị trên UI, ví dụ RWD-001 (cột "Reward ID").
   *
   * ⚠️ BE-MISSING N5 (C37 vẫn mở): **không có** trong response API #8
   * ⇒ cột `reward-code-list-dialog.component.html` "Reward ID" sẽ trống.
   */
  public rewardCode?: string
  /** ⚠️ BE trả `usedMallName: string` (đã localize) — service bọc thành TranslationDto. */
  public usedMallName?: TranslationDto
  /** `usedOfflineTenantId`. Physical Gift mặc định là "Information Desk" (FR-07-BR-02).
   *  ⚠️ BE trả string đã localize. */
  public usedOfflineTenantName?: TranslationDto
  /** ✅ ĐÃ CHỐT: `customerId` là `User.id` (không phải `ChallengeCustomer.id`). */
  public customerId?: string
  public customerName?: string
  /** ⚠️ BE trả tên `customerPhoneNumber`. */
  public phoneNumber?: string
  /** `ChallengeRewardCode.discountAmount` — số tiền giảm thực tế, chỉ Virtual Gift. */
  public discountAmount?: number
  public transactionAmount?: number
  /** `claimedAt` — thời điểm khách đổi/nhận mã. ISO Date string. ✅ khớp BE. */
  public claimedAt?: string
  /** `usedAt` — thời điểm mang mã đi dùng. ISO Date string. ✅ khớp BE. */
  public usedAt?: string
  public status?: RewardCodeStatus
  /** API #8 — mới. Có sẵn per-row nên không cần suy từ summary. */
  public rewardType?: RewardType
  /** API #8 — mới (string đã localize). */
  public rewardName?: string
  /**
   * API #8 — nhân viên đã quét/áp dụng mã.
   * ✅ ĐÃ CHỐT: hiển thị `usedByName` khi `status = USED`; `usedById` chỉ dùng nội bộ/để
   * link sau này, không hiển thị trực tiếp. Xem cột "Used By" ở `reward-code-list-dialog`.
   */
  public usedById?: string
  public usedByName?: string
  /** API #8 — mới, id thô kèm theo tên. */
  public usedMallId?: string
  public usedOfflineTenantId?: string

  public static create(data?: Partial<RewardCodeDto>): RewardCodeDto {
    const instance = new RewardCodeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-05 — chi tiết quà đã phát cho khách hàng.
 *
 * API #5 `GET /challenge-rewards` — BE chỉ trả **5 field** cho mỗi row.
 */
export class RewardHistoryItemDto {
  /** ⚠️ BE trả tên `rewardId`. */
  public id?: string
  /**
   * Mã quà hiển thị trên UI (cột "Reward ID").
   *
   * ⚠️ BE-MISSING N5 (C37 vẫn mở): **không có** trong response API #5
   * ⇒ cột đầu bảng `reward-history.component.html` sẽ trống.
   */
  public rewardCode?: string
  /** ⚠️ BE trả `rewardName: string` (đã localize) — service bọc thành TranslationDto. */
  public rewardName?: TranslationDto
  /** `ChallengeRewardCode.claimedAt` — thời điểm khách hàng đổi quà. ✅ khớp BE. */
  public claimedAt?: string
  public customerName?: string
  /** ⚠️ BE trả tên `customerPhoneNumber`. */
  public phoneNumber?: string

  public static create(data?: Partial<RewardHistoryItemDto>): RewardHistoryItemDto {
    const instance = new RewardHistoryItemDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-06 — tham số tìm kiếm và lọc Reward History (API #5).
 * ⚠️ `challengeId` giờ là **query param** (trước là path); `fromDate`/`toDate` map sang
 * `from`/`to`.
 */
export class FindRewardHistoryDto {
  public challengeId?: string
  /** Tìm theo Customer name hoặc Phone number. ✅ khớp BE. */
  public search?: string
  public rewardOptionId?: string
  /** API #5 — filter theo trạng thái code. UI hiện chưa có control này. */
  public status?: RewardCodeStatus
  /** ISO Date string → query `from`. */
  public fromDate?: string
  /** ISO Date string → query `to`. */
  public toDate?: string
  public page?: number
  public pageSize?: number

  public static create(data?: Partial<FindRewardHistoryDto>): FindRewardHistoryDto {
    const instance = new FindRewardHistoryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-02 — tham số lấy danh sách code của một Reward Option (API #8).
 * ⚠️ `challengeId` **không còn dùng cho request** (path mới chỉ có `rewardOptionId`),
 * giữ lại để component truyền context.
 */
export class FindRewardCodeDto {
  public challengeId?: string
  public rewardOptionId?: string
  public search?: string
  /** API #8 — filter theo trạng thái code. UI hiện chưa có control này. */
  public status?: RewardCodeStatus
  /** ISO Date string → query `from`. */
  public fromDate?: string
  /** ISO Date string → query `to`. */
  public toDate?: string
  public page?: number
  public pageSize?: number

  public static create(data?: Partial<FindRewardCodeDto>): FindRewardCodeDto {
    const instance = new FindRewardCodeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-07-BR-06 — payload export.
 *
 * API #7 `POST /challenge-rewards/export-excel` — BE gửi file về email người dùng.
 * ✅ ĐÃ CHỐT: giữ export qua email làm mặc định cho Reward History vì dữ liệu có thể lớn.
 * API #6 `GET /challenge-rewards/export` (tải trực tiếp) chỉ nên dùng cho tập dữ liệu nhỏ —
 * xem `AdminRewardHistoryService.downloadExcel()`.
 */
export class ExportRewardHistoryDto {
  public challengeId?: string
  public email?: string
  /** ⚠️ BE nhận `from` dạng datetime string — service convert từ `Date`. */
  public startDate?: Date
  /** ⚠️ BE nhận `to` dạng datetime string — service convert từ `Date`. */
  public endDate?: Date
  public rewardOptionId?: string
  public search?: string
  /** API #7 — filter theo trạng thái code. UI hiện chưa có control này. */
  public status?: RewardCodeStatus

  public static create(data?: Partial<ExportRewardHistoryDto>): ExportRewardHistoryDto {
    const instance = new ExportRewardHistoryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
